import { useCallback, useEffect, useRef } from 'react';
import type { LessonVersionSaveData } from '../services/lessonService';
import { lessonService } from '../services/lessonService';
import { useEditorStore } from '../store/editorStore';

interface UseAutoSaveOptions {
  lessonId: string;
  version: number;
  data: LessonVersionSaveData;
  isDirty: boolean;
}

interface UseAutoSaveReturn {
  forceSave: () => Promise<void>;
}

const LOCAL_SAVE_INTERVAL = 10_000;
const SERVER_SYNC_INTERVAL = 30_000;
const LOCAL_KEY = (id: string, v: number) => `lessoncraft_draft_${id}_v${v}`;

export function useAutoSave({
  lessonId,
  version,
  data,
  isDirty,
}: UseAutoSaveOptions): UseAutoSaveReturn {
  const { setIsSaving, markSaved } = useEditorStore();
  const dataRef = useRef(data);
  const isDirtyRef = useRef(isDirty);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  const saveToLocal = useCallback(() => {
    if (isDirtyRef.current) {
      try {
        localStorage.setItem(
          LOCAL_KEY(lessonId, version),
          JSON.stringify({ data: dataRef.current, timestamp: new Date().toISOString() })
        );
      } catch {
        // localStorage may be full; ignore
      }
    }
  }, [lessonId, version]);

  const saveToServer = useCallback(async () => {
    if (!isDirtyRef.current) return;
    setIsSaving(true);
    try {
      await lessonService.saveVersion(lessonId, version, dataRef.current);
      localStorage.removeItem(LOCAL_KEY(lessonId, version));
      markSaved(); // sets hasUnsavedChanges=false, lastSaved=now, isSaving=false
    } catch {
      // silently fail — next interval will retry
      setIsSaving(false);
    }
  }, [lessonId, version, setIsSaving, markSaved]);

  const forceSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await lessonService.saveVersion(lessonId, version, dataRef.current);
      localStorage.removeItem(LOCAL_KEY(lessonId, version));
      markSaved();
    } catch {
      setIsSaving(false);
      throw new Error('Save failed');
    }
  }, [lessonId, version, setIsSaving, markSaved]);

  useEffect(() => {
    const localTimer = setInterval(saveToLocal, LOCAL_SAVE_INTERVAL);
    const serverTimer = setInterval(saveToServer, SERVER_SYNC_INTERVAL);
    return () => {
      clearInterval(localTimer);
      clearInterval(serverTimer);
    };
  }, [saveToLocal, saveToServer]);

  return { forceSave };
}
